import { useRef, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin } from 'lucide-react'
import {
  AddSelectedItem,
  ClearSetItem,
  MenuDivider,
  MenuFooter,
  ToggleItem,
  ViewSetItem,
} from './set-context-menu/set-context-menu.parts'
import { clampMenuPosition, useContextMenuClose } from './set-context-menu/use-context-menu-close'

const MENU_WIDTH_PX = 220
const MENU_HEIGHT_PX = 250

interface MapButtonContextMenuProps {
  x: number
  y: number
  isInMapSet: boolean
  hasSelection: boolean
  selectionCount: number
  mapSetCount: number
  onClose: () => void
  onToggle: () => void
  onAddSelected: () => void
  onViewMapSet: () => void
  onClearMapSet: () => void
}

export function MapButtonContextMenu(props: MapButtonContextMenuProps): ReactElement {
  const {
    x,
    y,
    isInMapSet,
    hasSelection,
    selectionCount,
    mapSetCount,
    onClose,
    onToggle,
    onAddSelected,
    onViewMapSet,
    onClearMapSet,
  } = props
  const { t } = useTranslation('cases')
  const menuRef = useRef<HTMLDivElement>(null)
  useContextMenuClose(menuRef, onClose)
  const position = clampMenuPosition({ x, y, widthPx: MENU_WIDTH_PX, heightPx: MENU_HEIGHT_PX })
  const wrap = (action: () => void) => () => {
    action()
    onClose()
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
      style={position}
    >
      <ToggleItem
        isInSet={isInMapSet}
        addLabel={t('infrastructure.addToMap')}
        removeLabel={t('infrastructure.removeFromMap')}
        onClick={wrap(onToggle)}
      />
      <AddSelectedItem
        hasSelection={hasSelection}
        selectionCount={selectionCount}
        label={t('infrastructure.addSelectedToMap')}
        enabledTitle={t('infrastructure.addSelectedToMapTitle', { count: selectionCount })}
        disabledTitle={t('infrastructure.selectEndpointsFirst')}
        onClick={wrap(onAddSelected)}
      />
      <MenuDivider />
      <ViewSetItem
        label={t('infrastructure.viewMapSet')}
        count={mapSetCount}
        onClick={wrap(onViewMapSet)}
      />
      <MenuDivider />
      <ClearSetItem
        label={t('infrastructure.clearAllFromMap')}
        count={mapSetCount}
        onClick={wrap(onClearMapSet)}
      />
      <MenuFooter icon={MapPin} message={t('infrastructure.mapSetSharedWithTeam')} />
    </div>
  )
}
